import { useEffect, useMemo } from "react";
import FormItem from "@components/FormItem/FormItem";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { CARGO_ONBOARD_FORM_FIELDS } from "@features/vessel-nomination/add/constants";
import {
  getCargoOnboardParcelSchema,
  type CargoOnboardParcelFormData,
  type CargoOnboardParcelValidatedFormData,
} from "@features/vessel-nomination/add/schema/vesselNominationFormSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { operationTypes, REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import type { Cargo, CustomerTerminal } from "@services/vessel-nomination/types";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ParcelBindings } from "./parcelBindings";

const LANG_BASE = "vessel-nomination.add.form.parcels.side-drawer";

type Props = {
  parcels: ParcelBindings["parcels"];
  setParcels: ParcelBindings["setParcels"];
  clearParcelsError: ParcelBindings["clearParcelsError"];
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  parcelToEdit?: CargoOnboardParcelFormData;
  editingParcelIndex?: number | null;
};

const AddParcelSideDrawer = ({
  parcels,
  setParcels,
  clearParcelsError,
  isOpen,
  onClose,
  onSuccess,
  parcelToEdit,
  editingParcelIndex,
}: Props) => {
  const { t } = useTranslation();
  const isEditMode = editingParcelIndex !== null && editingParcelIndex !== undefined;

  const form = useForm<CargoOnboardParcelFormData, undefined, CargoOnboardParcelValidatedFormData>({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(getCargoOnboardParcelSchema(t)),
    shouldUnregister: true,
    defaultValues: {
      [CARGO_ONBOARD_FORM_FIELDS.TERMINAL]: "",
      [CARGO_ONBOARD_FORM_FIELDS.OPERATION_TYPE]: "",
      [CARGO_ONBOARD_FORM_FIELDS.CARGO_TYPE]: "",
      [CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_CBM]: undefined,
      [CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_MT]: undefined,
    },
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (parcelToEdit) {
      form.reset(parcelToEdit);
      return;
    }

    form.reset({
      [CARGO_ONBOARD_FORM_FIELDS.TERMINAL]: "",
      [CARGO_ONBOARD_FORM_FIELDS.OPERATION_TYPE]: "",
      [CARGO_ONBOARD_FORM_FIELDS.CARGO_TYPE]: "",
      [CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_CBM]: undefined,
      [CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_MT]: undefined,
    });
  }, [form, isOpen, parcelToEdit]);

  const onValid = (parcelData: CargoOnboardParcelValidatedFormData) => {
    const parcelFields = { ...parcelData };
    delete parcelFields.id;
    const updatedParcel =
      isEditMode && parcelToEdit?.id ? { ...parcelFields, id: parcelToEdit.id } : parcelFields;
    const updatedParcels = isEditMode
      ? parcels.map((parcel: CargoOnboardParcelFormData, index: number) =>
          index === editingParcelIndex ? updatedParcel : parcel,
        )
      : [...parcels, updatedParcel];

    setParcels(updatedParcels);
    clearParcelsError();
    form.reset();
    onClose();
    onSuccess();
  };

  const handleSubmit = () => {
    form.handleSubmit(onValid)();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const { options: cargoTypes } = useFormattedReference<Cargo>(
    `${REFERENCE_DATA_TYPES.CARGO}?cargoTypeCodes=LB,FO,CO`,
    "bff-vessel",
  );

  const cargoTypeOptions = useMemo(
    () =>
      cargoTypes.map((type) => ({
        label: String(type.cargoDescription),
        value: String(type.cargoCode),
      })),
    [cargoTypes],
  );

  const { options: customerTerminals } = useFormattedReference<CustomerTerminal>(
    REFERENCE_DATA_TYPES.CUSTOMER_TERMINAL,
    "bff-vessel",
  );

  const terminalOptions = useMemo(
    () =>
      customerTerminals.map((terminal) => ({
        label: terminal.companyNameEnglish || terminal.companyCode,
        value: terminal.companyCode,
      })),
    [customerTerminals],
  );

  return (
    <div data-testid="add-parcel-side-drawer">
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={handleSubmit}
          title={t(`${LANG_BASE}.${isEditMode ? "edit-parcel" : "add-parcel"}`)}
          submitLabel={t(`${LANG_BASE}.${isEditMode ? "edit-parcel" : "add-parcel"}`)}
        >
          <div className="w-[400px] p-6">
            <FormItem
              testId="add-parcel-terminal"
              name={CARGO_ONBOARD_FORM_FIELDS.TERMINAL}
              label={t(`${LANG_BASE}.terminal`)}
              type="autocomplete"
              options={terminalOptions}
            />
            <FormItem
              testId="add-parcel-operation-type"
              name={CARGO_ONBOARD_FORM_FIELDS.OPERATION_TYPE}
              label={t(`${LANG_BASE}.operation-type`)}
              type="autocomplete"
              options={operationTypes}
            />
            <FormItem
              testId="add-parcel-cargo-type"
              name={CARGO_ONBOARD_FORM_FIELDS.CARGO_TYPE}
              label={t(`${LANG_BASE}.cargo-type`)}
              type="autocomplete"
              options={cargoTypeOptions}
            />
            <FormItem
              testId="add-parcel-cargo-quantity-cbm"
              name={CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_CBM}
              label={t(`${LANG_BASE}.cargo-quantity-cbm`)}
              type="number"
              maxDigits={12}
              decimals={3}
              thousandSeparator
              adornment="CBM"
              canBeginWithZero={false}
            />
            <FormItem
              testId="add-parcel-cargo-quantity-mt"
              name={CARGO_ONBOARD_FORM_FIELDS.CARGO_QUANTITY_MT}
              label={t(`${LANG_BASE}.cargo-quantity-mt`)}
              type="number"
              maxDigits={12}
              decimals={3}
              thousandSeparator
              adornment="MT"
              canBeginWithZero={false}
            />
          </div>
        </SideDrawer>
      </FormProvider>
    </div>
  );
};

export default AddParcelSideDrawer;
